import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './istanbul3d.css';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { parseCoord } from './ilceKoordinatlari';

// İstanbul sınır kutusu (sol,üst,sağ,alt) — Nominatim aramasını İstanbul'a kısıtlar
const ISTANBUL_VIEWBOX = '27.90,41.72,29.98,40.75';

const MINI_STYLE = 'https://tiles.openfreemap.org/styles/dark';

/**
 * Adres arama (Nominatim/OSM) + tıklanabilir/sürüklenebilir pinli mini harita
 * + manuel Enlem/Boylam girişi. Form dialoglarında kullanılır.
 *
 * Props: enlem, boylam (string), onChange({enlem, boylam})
 */
export const AdresKonumSecici = ({ enlem, boylam, onChange, mapHeight = 240 }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null); // null = arama yapılmadı

  const setCoords = (lat, lon) => {
    onChangeRef.current({ enlem: lat.toFixed(6), boylam: lon.toFixed(6) });
  };

  const placeMarker = (lat, lon) => {
    const map = mapRef.current;
    if (!map) return;
    if (!markerRef.current) {
      const marker = new maplibregl.Marker({ color: '#f5a623', draggable: true })
        .setLngLat([lon, lat])
        .addTo(map);
      marker.on('dragend', () => {
        const p = marker.getLngLat();
        setCoords(p.lat, p.lng);
      });
      markerRef.current = marker;
    } else {
      markerRef.current.setLngLat([lon, lat]);
    }
  };

  // ---------- mini harita kurulumu ----------
  useEffect(() => {
    const lat = parseCoord(enlem);
    const lon = parseCoord(boylam);
    const hasCoords = isFinite(lat) && isFinite(lon);

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: MINI_STYLE,
      center: hasCoords ? [lon, lat] : [29.0, 41.02],
      zoom: hasCoords ? 14.5 : 8.7,
      attributionControl: { compact: true },
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right');
    map.on('load', () => map.resize());
    map.on('click', (e) => {
      placeMarker(e.lngLat.lat, e.lngLat.lng);
      setCoords(e.lngLat.lat, e.lngLat.lng);
    });
    mapRef.current = map;
    if (hasCoords) {
      // harita hazır olunca pini koy
      map.on('load', () => placeMarker(lat, lon));
    }
    return () => {
      if (markerRef.current) { markerRef.current.remove(); markerRef.current = null; }
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // ---------- dışarıdan (manuel input / arama) koordinat değişimi → pini taşı ----------
  useEffect(() => {
    const lat = parseCoord(enlem);
    const lon = parseCoord(boylam);
    const map = mapRef.current;
    if (!map || !isFinite(lat) || !isFinite(lon)) return;
    placeMarker(lat, lon);
    // Pin görünür alan dışındaysa haritayı oraya kaydır
    const b = map.getBounds();
    if (!b.contains([lon, lat])) {
      map.easeTo({ center: [lon, lat], zoom: Math.max(map.getZoom(), 13), duration: 700 });
    }
  }, [enlem, boylam]);

  const handleSearch = async () => {
    const q = query.trim();
    if (!q || searching) return;
    setSearching(true);
    setResults(null);
    try {
      const url =
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6&countrycodes=tr` +
        `&viewbox=${ISTANBUL_VIEWBOX}&bounded=1&accept-language=tr&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      setResults(Array.isArray(data) ? data : []);
    } catch (e) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const selectResult = (r) => {
    const lat = parseFloat(r.lat);
    const lon = parseFloat(r.lon);
    setCoords(lat, lon);
    setResults(null);
    setQuery(r.display_name.split(',').slice(0, 2).join(','));
    const map = mapRef.current;
    if (map) {
      placeMarker(lat, lon);
      map.flyTo({ center: [lon, lat], zoom: 16, duration: 1200, essential: true });
    }
  };

  return (
    <div className="p-3 bg-blue-900/20 border border-blue-700/40 rounded-lg space-y-3" data-testid="adres-konum-secici">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold text-blue-300 flex items-center gap-1">
          <span>📍</span> Konum Bilgisi (Adres Arama / Harita / Koordinat)
        </Label>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-[10px] border-blue-700/60 text-blue-200 hover:bg-blue-900/30"
            data-testid="coord-current-location-btn"
            onClick={() => {
              if (!navigator.geolocation) {
                alert('Tarayıcınız konum servisini desteklemiyor.');
                return;
              }
              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  setCoords(pos.coords.latitude, pos.coords.longitude);
                  if (mapRef.current) {
                    placeMarker(pos.coords.latitude, pos.coords.longitude);
                    mapRef.current.flyTo({ center: [pos.coords.longitude, pos.coords.latitude], zoom: 16, duration: 1200 });
                  }
                },
                (err) => alert('Konum alınamadı: ' + err.message),
                { enableHighAccuracy: true, timeout: 10000 }
              );
            }}
          >
            Mevcut Konumumu Al
          </Button>
          {enlem && boylam && (
            <a
              href={`https://www.google.com/maps?q=${enlem},${boylam}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center h-7 px-3 text-[10px] rounded-md border border-emerald-700/60 text-emerald-200 hover:bg-emerald-900/30"
              data-testid="coord-open-maps-btn"
            >
              Google Maps ↗
            </a>
          )}
        </div>
      </div>

      {/* Adres arama */}
      <div className="flex gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
          className="bg-gray-800 border-gray-700 text-white h-9 flex-1"
          placeholder="Adres ara... (örn. Yakacık Kartal, Bağdat Cad. Kadıköy)"
          data-testid="adres-arama-input"
        />
        <Button
          type="button"
          onClick={handleSearch}
          disabled={searching || !query.trim()}
          className="h-9 bg-blue-600 hover:bg-blue-700 text-white px-4"
          data-testid="adres-arama-btn"
        >
          {searching ? 'Aranıyor…' : '🔍 Ara'}
        </Button>
      </div>

      {/* Arama sonuçları */}
      {results !== null && (
        <div className="rounded-lg border border-gray-700 bg-gray-900/80 overflow-hidden" data-testid="adres-sonuc-listesi">
          {results.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-2.5">
              Sonuç bulunamadı. Daha genel bir arama deneyin (örn. sadece mahalle + ilçe).
            </p>
          ) : (
            results.map((r) => (
              <button
                type="button"
                key={r.place_id}
                onClick={() => selectResult(r)}
                className="w-full text-left px-3 py-2 text-xs text-gray-200 hover:bg-blue-900/40 border-b border-gray-800 last:border-b-0"
                data-testid="adres-sonuc-item"
              >
                <span className="text-blue-300 mr-1">📌</span>
                {r.display_name}
              </button>
            ))
          )}
        </div>
      )}

      {/* Mini harita */}
      <div className="relative rounded-lg overflow-hidden border border-gray-700" style={{ height: mapHeight }}>
        {/* NOT: maplibre-gl.css `.maplibregl-map{position:relative}` Tailwind `absolute`'u ezer — inline style şart */}
        <div
          ref={mapContainerRef}
          style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, width: '100%', height: '100%' }}
          data-testid="mini-harita"
        />
        <div className="absolute top-2 left-2 z-10 text-[10px] text-gray-300 bg-gray-950/70 rounded px-2 py-1 pointer-events-none">
          Haritaya tıklayın veya pini sürükleyin
        </div>
      </div>

      {/* Manuel koordinat girişi */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-gray-300">Enlem (Latitude)</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={enlem || ''}
            onChange={(e) => onChange({ enlem: e.target.value, boylam: boylam || '' })}
            className="bg-gray-800 border-gray-700 text-white h-9"
            placeholder="Örn. 41.008238"
            data-testid="enlem-input"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-gray-300">Boylam (Longitude)</Label>
          <Input
            type="text"
            inputMode="decimal"
            value={boylam || ''}
            onChange={(e) => onChange({ enlem: enlem || '', boylam: e.target.value })}
            className="bg-gray-800 border-gray-700 text-white h-9"
            placeholder="Örn. 28.978359"
            data-testid="boylam-input"
          />
        </div>
      </div>
      <p className="text-[11px] text-blue-300/70">
        Adres arayabilir, haritaya tıklayabilir, pini sürükleyebilir veya koordinatı elle girebilirsiniz. WGS84 (ondalık derece) formatı desteklenir.
      </p>
    </div>
  );
};
